<template>
  <div>
    <v-row dense row wrap justify="center">
      <v-col v-if="loadingQueries" class="col-12">
        <Loader text=""></Loader>
      </v-col>
      <div class="hyp-save-search-container">
        <v-row
          dense
          v-if="
            !loadingQueries &&
            savedQueries &&
            savedQueries.length &&
            !showNameForm
          "
        >
          <v-col>
            <v-select
              :items="savedQueries"
              v-model="selectedQuery"
              item-text="name"
              :item-value="savedSearchIdPropertyName"
              label="Select Existing Queries"
              return-object
              single-line
              v-on:change="changeSelectedQuery"
              :clearable="true"
            >
              <template v-slot:selection="{ item }">
                <v-icon
                  v-if="item.frequency"
                  size="16"
                  class="mr-2"
                  title="Notification set"
                  >fas fa-bell</v-icon
                >
                <v-icon
                  v-if="item.shared"
                  size="16"
                  class="mr-2"
                  title="Shared with me"
                  >fas fa-share</v-icon
                >
                {{ item.name }}
              </template>
              <template v-slot:item="{ item }">
                <v-icon
                  v-if="item.frequency"
                  size="16"
                  class="mr-2"
                  title="Notification set"
                  >fas fa-bell</v-icon
                >
                <v-icon
                  v-if="item.shared"
                  size="16"
                  class="mr-2"
                  title="Shared with me"
                  >fas fa-share</v-icon
                >
                {{ item.name }}
              </template>
            </v-select>
          </v-col>
          <v-col
            cols="1"
            v-if="!loadingQueries && selectedQuery"
            class="save-search-menu-container"
          >
            <v-menu open-on-click offset-x right rounded="lg">
              <template v-slot:activator="{ on: menu }">
                <v-icon
                  v-on="menu"
                  class="pull-right hyp-options-icon"
                  size="14"
                  >fas fa-ellipsis-v</v-icon
                >
              </template>

              <v-list>
                <v-list-item @click="handleFormToggle">
                  <v-list-item-title> Rename Search </v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="!selectedQuery.shared"
                  @click="handleClickShareSearch"
                >
                  <v-list-item-title> Share Search </v-list-item-title>
                </v-list-item>
                <v-list-item @click="handleDeleteClick">
                  <v-list-item-title>
                    <v-progress-circular
                      v-if="loadingQueries"
                      indeterminate
                      color="#0000008a"
                      size="16"
                      class="mr-1"
                    >
                    </v-progress-circular>
                    Delete Search
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>
        <span>
          <v-row dense v-if="errors.length && showNameForm">
            <p>
              <span class="application-heading"
                >Please correct the following error(s):</span
              >
            </p>
            <ul>
              <li v-for="error in errors" :key="error">
                {{ error }}
              </li>
            </ul>
          </v-row>
          <v-row dense>
            <v-col class="col-12">
              <div ref="form">
                <v-row dense align="center">
                  <v-text-field
                    ref="nameField"
                    v-model="fields.name"
                    :label="nameLabel"
                    placeholder="Enter a query name"
                    class="hyp-form-text"
                    v-if="showNameForm"
                    v-on:keyup.enter="handleFormSave"
                  ></v-text-field>
                  <span
                    style="padding: 2px;"
                    @click="handleFormSave"
                    v-if="showNameForm"
                  >
                    <v-icon small style="color: #008000;">fas fa-check</v-icon>
                  </span>
                  <span
                    style="padding: 2px;"
                    @click="handleFormClose"
                    v-if="showNameForm"
                  >
                    <v-icon small style="color: #ff0000;">fas fa-times</v-icon>
                  </span>
                </v-row>
                <v-row dense>
                  <v-btn
                    v-if="!selectedQuery && !showNameForm"
                    block
                    color="primary"
                    medium
                    @click.once="handleFormToggle"
                  >
                    <span class="save-search-text">Save Search</span>
                  </v-btn>
                </v-row>

                <span v-if="selectedQuery && !showNameForm">
                  <v-row dense align="center">
                    <v-col>
                      <v-btn
                        block
                        color="primary"
                        medium
                        @click.once="handleSaveAs"
                      >
                        <span class="save-search-text">Save As</span>
                      </v-btn>
                    </v-col>
                    <v-col>
                      <v-btn
                        block
                        color="secondary"
                        medium
                        @click="handleModifySearch"
                        :disabled="isDisabled"
                      >
                        Save
                      </v-btn>
                    </v-col>
                  </v-row>
                </span>
              </div>
            </v-col>
          </v-row>
        </span>
      </div>
    </v-row>

    <SearchUserShareModal
      v-if="selectedQuery"
      :saved-search="selectedQuery"
      :open="userShareModalOpen"
      @close="handleCloseShareModal"
    ></SearchUserShareModal>

    <ConfirmationPopup
      :show="deleteConfirmationShow"
      :title="`Delete : ${queryName}`"
      :text="`Are you sure you want to delete the saved query <strong>${queryName}</strong>?`"
      :buttonCancel="deleteConfirmationBtnCancel"
      :buttonAction="deleteConfirmationBtnAction"
    ></ConfirmationPopup>
  </div>
</template>

<script>
import lodashSortBy from "lodash/sortBy";
import lodashIsEqual from "lodash/isEqual";
import lodashHas from "lodash/has";
import lodashGet from "lodash/get";
import lodashCloneDeep from "lodash/cloneDeep";

import { mapGetters } from "vuex";
import {
  MODULE_PREFIX_SAVED_SEARCH,
  MODULE_PREFIX_USER,
  MODULE_PREFIX_PLACES,
} from "@/store";
import {
  GET_SAVED_SEARCH_QUERIES,
  DELETE_SAVED_SEARCH_QUERY,
  GET_PLACES,
  GET_SAVED_SEARCH_DETAILS,
} from "@/store/actions.type";
import {
  saveSearchQuery,
  updateSearchQuery,
} from "@/services/search/savedSearch.service";
import { formatSavedSearchParams } from "@/services/search/search.format";
import { isDateStringEqual } from "@/common/date.utilities";
import { isBoundsStringEqual } from "@/common/maps/utilities";
import { EVENT_ACTIONS, SEARCH_CATEGORIES } from "@/common/constants/analytics";
import { getSavedSearchIdPropertyName } from "@/services/notifications/notifications.service";

const EMITTED_ACTIONS = {
  CHANGE: "change",
};

const MAX_NAME_LENGTH = 50;

export default {
  name: "SearchFilterSavedQueries",
  components: {
    Loader: () => import("@/components/common/Loader"),
    ConfirmationPopup: () => import("@/components/common/ConfirmationPopup"),
    SearchUserShareModal: () =>
      import("@/components/search/SearchUserShareModal"),
  },
  props: {
    initialize: {
      type: String,
      default: null,
    },
    selected: {
      type: Object,
    },
    reset: {
      type: String,
    },
    defaultSavedSearchId: {
      type: String,
    },
  },
  async mounted() {
    await this.loadQueries();
  },
  data() {
    return {
      savedQueries: [],
      loadingQueries: true,
      deleteConfirmationShow: false,
      queryName: null,
      selectedQuery: null,
      fields: {
        name: "",
        description: "",
      },
      errors: [],
      showNameForm: false,
      userShareModalOpen: false,
    };
  },
  computed: {
    ...mapGetters({
      queries: MODULE_PREFIX_SAVED_SEARCH + "queries",
      preferences: MODULE_PREFIX_USER + "preferences",
      places: MODULE_PREFIX_PLACES + "places",
      savedSearchDetails: MODULE_PREFIX_SAVED_SEARCH + "savedSearchDetails",
    }),
    savedSearchIdPropertyName() {
      return getSavedSearchIdPropertyName();
    },
    deleteConfirmationBtnCancel() {
      return {
        text: "Cancel",
        onClickFnc: () => {
          this.setDeleteConfirmationShow(false);
        },
      };
    },
    deleteConfirmationBtnAction() {
      return {
        text: "Yes",
        disabled: this.deleteBtnDisabled,
        loader: true,
        onClickFnc: () => {
          this.deleteSavedQuery(
            this.selectedQuery[this.savedSearchIdPropertyName]
          );
        },
      };
    },
    nameLabel() {
      return this.selectedQuery ? "Rename" : "Name";
    },
    isDisabled() {
      //TODO: This does not take all available fields into consideration
      let params = null;
      let selectedQueryParams = this.selectedQuery.params;
      if (lodashHas(this.preferences, "liveSearch")) {
        params = lodashGet(this.preferences, "liveSearch");
      }
      const {
        ["bounds"]: bounds,
        ["boundsType"]: boundsType,
        ["search"]: search,
        ["start"]: start,
        ["end"]: end,
        ["topic"]: topic,
        ["source"]: source,
        ["sources"]: sources,
        ["mapType"]: mapType,
        ["eventType"]: eventType,
        ["riskCategories"]: riskCategories,
        ["topics"]: topics,
        ["keywordsBuilder"]: keywordsBuilder,
        ["tags"]: tags,
      } = params;

      //TODO figure out how to get this set correctly
      //its here so that a freshly saved search where the user
      //never touched the start handle isn't considered modified
      if (!selectedQueryParams.end) {
        selectedQueryParams.end = end;
      }

      return (
        (this.selectedQuery && this.selectedQuery.shared) ||
        (isBoundsStringEqual(selectedQueryParams.bounds, bounds) &&
          selectedQueryParams.bounds_type === boundsType &&
          isDateStringEqual(selectedQueryParams.start, start) &&
          isDateStringEqual(selectedQueryParams.end, end) &&
          JSON.stringify(selectedQueryParams.places) ===
            JSON.stringify(selectedQueryParams.places) &&
          selectedQueryParams.search === search &&
          (selectedQueryParams.topics
            ? selectedQueryParams.topics.sort().join(",")
            : null) === (topic ? topic.split(",").sort().join(",") : null) &&
          (selectedQueryParams.sources
            ? selectedQueryParams.sources.sort().join(",")
            : null) === (source ? source.split(",").sort().join(",") : null))
      );
    },
  },
  methods: {
    async loadQueries(skipGet) {
      this.loadingQueries = true;
      let SORTED = [];
      try {
        if (!skipGet) {
          await this.$store.dispatch(
            MODULE_PREFIX_SAVED_SEARCH + GET_SAVED_SEARCH_QUERIES,
            {}
          );
        }
        this.queries.forEach((query) => {
          query.shared = query.owner_id !== query.user_id;
        });
        SORTED = lodashSortBy(this.queries, "name");
      } catch (error) {
        console.error("Error in loadQueries: " + JSON.stringify(error));
      }
      this.savedQueries = SORTED;
      this.loadingQueries = false;
    },
    deleteSavedQuery(id) {
      try {
        this.$store.dispatch(
          MODULE_PREFIX_SAVED_SEARCH + DELETE_SAVED_SEARCH_QUERY,
          { id: id }
        );

        this.handlePageTrackerEvent(
          SEARCH_CATEGORIES.live_search,
          EVENT_ACTIONS.clicked,
          "Delete"
        );
      } catch (error) {
        console.error("There was an error deleting the saved query");
      } finally {
        this.setDeleteConfirmationShow(false);
        this.changeSelectedQuery(null);
      }
    },
    handleDeleteClick() {
      this.queryName = this.selectedQuery.name;
      this.setDeleteConfirmationShow(true);
    },
    handleClickShareSearch() {
      this.userShareModalOpen = true;
    },
    async handleCloseShareModal() {
      this.userShareModalOpen = false;
      await this.loadQueries();
    },
    setDeleteConfirmationShow(value) {
      this.deleteConfirmationShow = value;
    },
    changeSelectedQuery(query) {
      this.$store.dispatch(
        MODULE_PREFIX_SAVED_SEARCH + GET_SAVED_SEARCH_QUERIES,
        {}
      );
      this.manageSelectedQuery(query);
      this.$emit(EMITTED_ACTIONS.CHANGE, this.selectedQuery);
    },
    manageSelectedQuery(query) {
      this.fields.name = query ? query.name : null;

      this.$store.dispatch(
        MODULE_PREFIX_SAVED_SEARCH + GET_SAVED_SEARCH_DETAILS,
        query
      );
      this.$emit("change", query);

      this.handlePageTrackerEvent(
        SEARCH_CATEGORIES.live_search,
        EVENT_ACTIONS.selected,
        "Saved Search"
      );
    },
    clearSelectedQuery() {
      this.$store.dispatch(
        MODULE_PREFIX_SAVED_SEARCH + GET_SAVED_SEARCH_DETAILS,
        null
      );
    },
    async handleModifySearch() {
      this.loadingQueries = true;
      try {
        let _params, result;
        if (lodashHas(this.preferences, "liveSearch")) {
          _params = lodashGet(this.preferences, "liveSearch");
        }
        let places = null;
        if (
          (this.selectedQuery && this.selectedQuery.places) ||
          _params.places
        ) {
          const ps =
            this.selectedQuery && this.selectedQuery.places
              ? this.selectedQuery.places
              : _params.places;
          places = this.getSelectedPlacesArray(
            ps,
            _params.placesRadius || null,
            _params.placesPoisDistUnits || null
          );
        }
        _params.places = places;

        let body = {
          name: this.fields.name ? this.fields.name : this.selectedQuery.name,
          description: this.fields.description
            ? this.fields.description
            : this.selectedQuery.description,
          frequency: this.fields.frequency
            ? this.fields.frequency
            : this.selectedQuery.frequency,
          params: formatSavedSearchParams(_params),
        };

        // add notification props if already there
        if (this.selectedQuery.shared_list) {
          body.shared_list = this.selectedQuery.shared_list;
        }
        if (this.selectedQuery.use_email) {
          body.use_email = this.selectedQuery.use_email;
        }
        if (this.selectedQuery.use_push) {
          body.use_push = this.selectedQuery.use_push;
        }
        if (this.selectedQuery.use_integrations) {
          body.use_integrations = this.selectedQuery.use_integrations;
        }
        if (this.selectedQuery.integration_targets) {
          body.integration_targets = this.selectedQuery.integration_targets;
        }
        if (this.selectedQuery.only_first_report) {
          body.only_first_report = this.selectedQuery.only_first_report;
        }
        if (this.selectedQuery.pause) {
          body.pause = this.selectedQuery.pause;
        }
        if (this.selectedQuery.expiration) {
          body.expiration = this.selectedQuery.expiration;
        }
        if (this.selectedQuery.expiration_date) {
          body.expiration_date = this.selectedQuery.expiration_date;
        }
        if (this.selectedQuery.schedule) {
          body.schedule = this.selectedQuery.schedule;
        }

        result = await updateSearchQuery({
          id: this.selectedQuery[this.savedSearchIdPropertyName],
          data: body,
        });

        await this.$store.dispatch(
          MODULE_PREFIX_SAVED_SEARCH + GET_SAVED_SEARCH_DETAILS,
          result
        );

        this.changeSelectedQuery(result);
        this.showNameForm = false;

        this.handlePageTrackerEvent(
          SEARCH_CATEGORIES.live_search,
          EVENT_ACTIONS.clicked,
          "Save Update"
        );
      } catch (error) {
        console.error(
          "There was an error updating the saved query " +
            this.selectedQuery.name
        );
      }
      this.loadingQueries = false;
    },
    async handleSaveSearch() {
      this.loadingQueries = true;
      let _params;
      if (this.params) {
        _params = this.params;
      }
      if (!_params && lodashHas(this.preferences, "liveSearch")) {
        _params = lodashGet(this.preferences, "liveSearch");
      }
      if (_params && this.validForm()) {
        let places = null;
        if (_params.places) {
          places = this.getSelectedPlacesArray(
            _params.places,
            _params.placesRadius || null,
            _params.placesPoisDistUnits || null
          );
        }
        _params.places = places;
        let body = {
          name: this.fields.name,
          description: this.fields.description,
          params: formatSavedSearchParams(_params),
        };
        let result;
        try {
          result = await saveSearchQuery(body);
          await this.$store.dispatch(
            MODULE_PREFIX_SAVED_SEARCH + GET_SAVED_SEARCH_QUERIES,
            {}
          );
          this.manageSelectedQuery(result);
        } catch (error) {
          console.error("There was an error when saving the query: " + error);
        }
        this.showNameForm = !this.showNameForm;

        this.handlePageTrackerEvent(
          SEARCH_CATEGORIES.live_search,
          EVENT_ACTIONS.clicked,
          "Save New"
        );
      }
      this.loadingQueries = false;
    },
    validForm() {
      this.errors = [];

      if (!this.fields.name) {
        this.errors.push("Name required.");
        return false;
      }
      if (this.fields.name.length > MAX_NAME_LENGTH) {
        this.errors.push(`Name cannot exceed ${MAX_NAME_LENGTH} characters.`);
        return false;
      }
      return true;
    },
    async handleFormSave() {
      if (
        this.selectedQuery &&
        this.selectedQuery[this.savedSearchIdPropertyName]
      ) {
        await this.handleModifySearch();
      } else {
        await this.handleSaveSearch();
      }
    },
    handleFormToggle() {
      if (this.selectedQuery) {
        this.fields.name = this.selectedQuery ? this.selectedQuery.name : null;
      } else {
        this.resetForm();
      }
      this.showNameForm = !this.showNameForm;
      this.$nextTick(() => {
        this.$refs.nameField.focus();
      });
    },
    resetForm() {
      this.errors = [];
      this.fields = {
        name: "",
        description: "",
      };
      this.clearSelectedQuery();
    },
    handleFormClose() {
      this.showNameForm = !this.showNameForm;
    },
    handleSaveAs() {
      delete this.selectedQuery[this.savedSearchIdPropertyName];
      this.selectedQuery.name = "";
      this.handleFormToggle();
      this.handlePageTrackerEvent(
        SEARCH_CATEGORIES.live_search,
        EVENT_ACTIONS.clicked,
        "Save As"
      );
    },
    async getPlaces() {
      await this.$store.dispatch(MODULE_PREFIX_PLACES + GET_PLACES);
    },
    getSelectedPlacesArray(selected, radius, poisDistUnits) {
      // places: [[lat,lon,radius,place_id]]
      const places = [];
      this.places.forEach((layer) => {
        if (lodashGet(layer, "data.features") && layer.data.features.length) {
          const len = layer.data.features.length;
          for (let i = 0; i < len; i++) {
            const place = layer.data.features[i];
            if (
              lodashGet(place, "id") &&
              selected.includes(place.id) &&
              lodashGet(place, "geometry.coordinates")
            ) {
              const arr = [
                place.geometry.coordinates[0],
                place.geometry.coordinates[1],
                radius,
                place.id,
                poisDistUnits,
              ];
              places.push(arr);
            }
          }
        }
      });
      return places;
    },
  },
  watch: {
    queries: {
      async handler(newValue, oldValue) {
        if (!lodashIsEqual(newValue, oldValue)) {
          this.items = null;
          this.itemsPage = 1;
          this.itemsCount = 0;
          this.loading = true;
          await this.loadQueries(true);
        }
      },
    },
    savedSearchDetails: {
      handler(newValue, oldValue) {
        if (!lodashIsEqual(newValue, oldValue)) {
          this.selectedQuery = lodashCloneDeep(this.savedSearchDetails);
          this.$emit(EMITTED_ACTIONS.CHANGE, this.selectedQuery);
        }
      },
    },
    initialize: {
      async handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          await this.$store.dispatch(
            MODULE_PREFIX_SAVED_SEARCH + GET_SAVED_SEARCH_QUERIES,
            { force: true }
          );
        }
      },
      immediate: true,
    },
    selected: {
      async handler(newValue, oldValue) {
        if (!newValue) {
          this.selectedQuery = null;
          this.resetForm();
        }
        if (!lodashIsEqual(newValue, oldValue)) {
          this.selectedQuery = lodashCloneDeep(this.savedSearchDetails);
          this.$emit(EMITTED_ACTIONS.CHANGE, this.selectedQuery);
        }
      },
      immediate: true,
    },
    reset: {
      handler() {
        this.resetForm();
      },
    },
  },
};
</script>

<style scoped>
.hyp-save-search-container {
  padding: 0;
  margin: 0;
  width: 100%;
}

.save-search-text {
  display: flex;
  flex-direction: column;
  font-size: 14px;
  color: rgba(255, 255, 255, 255);
}

.save-search-delete-btn-container {
  padding-top: 12px;
  margin-top: 4px;
}

.save-search-menu-container {
  padding: 6px;
  margin-top: 24px;
}
</style>
